import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Heart } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import VerifiedBadge from "../components/VerifiedBadge";

function profileImage(profile) {
  if (profile.photos?.length > 0) return fileUrl(profile.photos[0]);
  if (profile.photo_url) return profile.photo_url;
  return null;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/conversations");
        setConversations(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 md:py-12" data-testid="messages-page">
      <div className="mb-10">
        <div className="text-gold-soft tracking-overline text-xs mb-2">MESSAGES</div>
        <h1 className="font-display text-4xl font-light">Conversations that matter.</h1>
      </div>

      {loading ? (
        <div className="text-gold-soft tracking-overline text-xs">LOADING</div>
      ) : conversations.length === 0 ? (
        <div className="glass-strong rounded-3xl p-12 text-center" data-testid="messages-empty">
          <MessageCircle className="mx-auto text-gold mb-6" size={32} />
          <h2 className="font-display text-2xl font-light mb-3">No conversations yet.</h2>
          <p className="text-white/55 leading-relaxed max-w-md mx-auto mb-8">
            When you and someone both like each other, you'll be able to message here.
          </p>
          <Link to="/discover" className="btn-gold inline-block" data-testid="goto-discover">
            <span className="inline-flex items-center gap-2"><Heart size={14} /> Start discovering</span>
          </Link>
        </div>
      ) : (
        <div className="glass-strong rounded-3xl overflow-hidden">
          {conversations.map((c, i) => {
            const img = profileImage(c.profile);
            const isUnread = c.unread_count > 0;
            const lastSenderIsMe = c.last_message?.sender_id && c.last_message.sender_id === c.profile.user_id ? false : true;
            const preview = c.last_message?.text || "You matched — say hello.";
            return (
              <motion.div
                key={c.match_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <Link
                  to={`/messages/${c.match_id}`}
                  className="flex items-center gap-4 p-5 hover:bg-white/[0.03] transition-colors border-b border-white/5 last:border-0"
                  data-testid={`conversation-${i}`}
                >
                  <div className="relative shrink-0">
                    {img ? (
                      <img src={img} alt={c.profile.name} className="w-14 h-14 rounded-full object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-[#1a1a1a] flex items-center justify-center">
                        <span className="font-display text-xl text-gold/50">{c.profile.name?.[0]}</span>
                      </div>
                    )}
                    {isUnread && (
                      <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#D4AF37] border-2 border-[#050505]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className={`font-display text-lg leading-tight flex items-center gap-1.5 ${isUnread ? "text-white" : "text-white/90"}`}>
                        {c.profile.name}
                        {c.profile.verified && <VerifiedBadge size={12} />}
                      </div>
                      <div className="text-xs text-white/40 shrink-0">
                        {c.last_message ? formatTime(c.last_message.created_at) : formatTime(c.matched_at)}
                      </div>
                    </div>
                    <div className={`text-sm truncate mt-1 ${isUnread ? "text-white" : "text-white/50"}`}>
                      {c.last_message && !lastSenderIsMe ? "You: " : ""}{preview}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
